import React, { CSSProperties, useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { AlignType } from "react-bootstrap/esm/types";
import { isEmpty, snakeCase } from "lodash";
import Icon from "./Icon";
import TextInputFilter from "./filter/TextInputFilter";
import { RenderWhen } from "./index";
import useWithFilter, { IWithFilter } from "../hooks/useWithFilter";

export interface DropdownFilterDataProps<T> {
  label: React.ReactNode;
  value: T;
  onClick?: (e: React.MouseEvent) => void;
  field?: string;
}

export type MenuItems<T> = Array<DropdownFilterDataProps<T>>;

export interface DropdownFilterProps<T> {
  disabled?: boolean;
  eventKey?: string | number;
  title: React.ReactNode;
  field: string;
  activeFilter?: T;
  handleFilterChange?: (filter: T) => void;
  menuItems?: MenuItems<T>;
  styles?: {
    button?: CSSProperties;
    menu?: CSSProperties;
  };
  menuItemStyles?: CSSProperties;
  menuAlign?: AlignType;
  containerClassName?: string | null;
  styleVariant?: string;
  CustomDropdownMenuComponent?: React.ElementType | null;
  CustomDropdownToggleComponent?: React.ElementType;
  iconName?: string | null;
  dropup?: boolean;
  chevronIcon?: boolean;
  autoClose?: boolean | "outside" | "inside";
  buttonClassname?: string;
  onlyPeriods?: boolean;
  formatLabel?: (item: DropdownFilterDataProps<T>) => JSX.Element;
  withSearch?: boolean;
  searchFilterPredicate?: (
    item: DropdownFilterDataProps<T>,
    searchValue?: string
  ) => boolean;
  dropdownSearchFilterField?: string;
  searchBoxPlaceholder?: string;
  dropdownToggleTitle?: string;
}

/**
 * Utility function that can generate structure for dropdown [menuItems prop] from plain object
 * prop values are used for labels, prop keys are used for value
 * @param obj
 * @return
 *
 * eg. {key1: value1, key2: value2}
 * [{
 *    label: <span> value1 </span>,
 *    value: key1
 *  },
 *  {
 *    label: <span> value2 </span>,
 *    value: key2
 *  },
 *  ]
 */
const convertObjectForDropdownDisplay = (obj: Record<string, string>) =>
  Object.entries(obj).map(([key, value]) => ({
    label: <span>{value}</span>,
    value: key
  }));

const DEFAULT_BUTTON_STYLES = {
  minWidth: "120px",
  textAlign: "left" as CSSProperties["textAlign"]
};

const DEFAULT_MENU_STYLES = {
  minWidth: "120px"
};

const DEFAULT_STYLES = {
  button: DEFAULT_BUTTON_STYLES,
  menu: DEFAULT_MENU_STYLES
};

function DropdownFilter<T>(props: DropdownFilterProps<T>) {
  const [show, setShow] = useState(false);
  const getActiveMenuItem = (menuItems: Array<DropdownFilterDataProps<T>>) => {
    const { activeFilter, title } = props;
    const activeIndex = Math.max(
      menuItems.findIndex(i => i.value === activeFilter),
      0
    );

    return {
      selectedLabel:
        activeIndex && !props.onlyPeriods
          ? menuItems[activeIndex].label
          : title,
      activeIndex
    };
  };

  const handleClick = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    menuItem: DropdownFilterDataProps<T>
  ) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    props.handleFilterChange?.(menuItem.value);
    setShow(false);
  };

  const toggleDropdown = () => {
    setShow(!show);
  };

  const {
    disabled = false,
    field,
    title,
    eventKey,
    styles = DEFAULT_STYLES,
    menuItemStyles = {},
    menuAlign = "start",
    containerClassName = "",
    styleVariant,
    CustomDropdownMenuComponent = null,
    CustomDropdownToggleComponent,
    iconName,
    dropup = false,
    chevronIcon = true,
    autoClose = true,
    buttonClassname,
    withSearch = false,
    searchFilterPredicate,
    dropdownSearchFilterField,
    searchBoxPlaceholder,
    dropdownToggleTitle
  } = props;
  const menuItems = useMemo(() => props.menuItems || [], [props.menuItems]);
  const { selectedLabel, activeIndex } = getActiveMenuItem(menuItems);

  const passProps = CustomDropdownMenuComponent
    ? {
        ...props,
        handleClick: (
          menuItem: DropdownFilterDataProps<T>,
          e: React.MouseEvent<HTMLElement, MouseEvent>
        ) => handleClick(e, menuItem),
        activeIndex
      }
    : {};
  const menuProps = isEmpty(menuItemStyles) ? {} : { style: menuItemStyles };
  const dropDirection = dropup ? "up" : "down";

  const containerClasses = `
    ${containerClassName || ""} ${styleVariant || ""}`;

  const [searchValue, setSearchValue] = useState("");
  const onSearchChange = (text: string) => {
    setSearchValue(text);
  };

  const dropdownResults = useMemo(
    () =>
      menuItems.filter(
        (item: DropdownFilterDataProps<T>) =>
          !withSearch ||
          (searchFilterPredicate
            ? searchFilterPredicate(item, searchValue)
            : true)
      ),
    [menuItems, withSearch, searchFilterPredicate, searchValue]
  );

  return (
    <Dropdown
      key={`${field}-${show}`} // (temp?) fix for issue with background color not updating when clicking on menu items
      id={`${field}-filter-dropdown-container`}
      className={containerClasses}
      drop={dropDirection}
      show={show}
      autoClose={autoClose}
      onToggle={() => toggleDropdown()}
      data-testid={snakeCase(`${field}-dropdown`)}
    >
      <Dropdown.Toggle
        id={`${field}-filter-dropdown`}
        as={CustomDropdownToggleComponent}
        disabled={disabled}
        variant="link"
        className={`${buttonClassname ?? ""} ${show ? "btn-link__active" : ""}`}
        style={{
          ...DEFAULT_BUTTON_STYLES,
          ...styles?.button
        }}
        data-testid={snakeCase(`${field}_dropdown_toggle`)}
        title={dropdownToggleTitle}
      >
        {iconName ? <Icon name={iconName} /> : null}
        {selectedLabel || title}
        {chevronIcon ? (
          <Icon name="chevron-down" className="dropdown-toggle__chevron-icon" />
        ) : null}
      </Dropdown.Toggle>
      <Dropdown.Menu
        align={menuAlign}
        {...passProps}
        {...menuProps}
        as={CustomDropdownMenuComponent || "div"}
        renderOnMount
        data-testid={snakeCase(`${field}_menu`)}
        style={{
          ...DEFAULT_MENU_STYLES,
          ...styles?.menu
        }}
      >
        <RenderWhen condition={!!withSearch}>
          <div className={"dropdown-filter-with-search__container"}>
            <div className={"dropdown-filter-with-search__search-box"}>
              <TextInputFilter
                extraClass={"dropdown-filter-with-search__search-input"}
                field={dropdownSearchFilterField || field}
                onChange={onSearchChange}
                placeholder={searchBoxPlaceholder || "Search"}
              />

              <div className={"dropdown-filter-with-search__search-icon"}>
                <Icon name={"search"} />
              </div>
            </div>
            <Dropdown.Divider
              className={"dropdown-filter-with-search__divider"}
            />
          </div>
        </RenderWhen>
        <RenderWhen condition={!!searchValue && dropdownResults.length === 0}>
          <div
            className={"flex justify-content-center align-center height-40-px"}
          >
            <span>No results found</span>
          </div>
        </RenderWhen>
        {dropdownResults.map((menuItem, idx) => (
          <Dropdown.Item
            key={`${idx}_${menuItem.field}`}
            eventKey={`${eventKey}.${idx}`}
            onClick={e => handleClick(e, menuItem)}
            active={menuItem.value === menuItems[activeIndex].value}
            data-testid={snakeCase(`${field}_menu_item_${menuItem.value}`)}
          >
            {props.formatLabel ? props.formatLabel(menuItem) : menuItem.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function DropdownWithFilter<T>(props: DropdownFilterProps<T> & IWithFilter<T>) {
  const { state, handleFilterChange } = useWithFilter(props);

  return (
    <DropdownFilter
      activeFilter={state.value}
      handleFilterChange={handleFilterChange}
      {...props}
    />
  );
}

export {
  DropdownWithFilter as default,
  DropdownFilter,
  convertObjectForDropdownDisplay
};
